import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import api from '@/api';
import {
  useEvalStudioScenariosArchiveCreate,
  useEvalStudioScenariosDisableCreate,
  useEvalStudioScenariosEnableCreate,
  useEvalStudioScenariosPartialUpdate,
  useEvalStudioScenariosPublishCreate,
  useEvalStudioScenariosRestoreCreate,
} from '@/gen-api/api';
import type { AgentList } from '@/gen-api/models';
import type {
  EvalRunDetail,
  EvalRunListItem,
  EvalRunStatus,
  ScenarioDetail,
  ScenarioListItem,
  ScenarioRunStatus,
  ScenarioRunSummary,
  ScenarioRunTrace,
  ScenarioVersion,
  ScenarioWithSingleRuns,
} from '@/types/eval-studio';

export function buildScenariosWithSingleRuns(
  scenarios: ScenarioListItem[],
  standaloneRuns: ScenarioRunSummary[],
  search?: string
): ScenarioWithSingleRuns[] {
  const scenariosById = new Map(scenarios.map((scenario) => [scenario.id, scenario]));
  const latestRunAtByScenario = new Map<string, string>();

  for (const run of standaloneRuns) {
    const current = latestRunAtByScenario.get(run.scenario_id);
    if (!current || run.created_at > current) {
      latestRunAtByScenario.set(run.scenario_id, run.created_at);
    }
  }

  const query = (search ?? '').trim().toLowerCase();

  return Array.from(latestRunAtByScenario.entries())
    .map(([scenarioId, latest_single_run_at]) => {
      const scenario = scenariosById.get(scenarioId);
      if (!scenario) {
        return null;
      }
      return { ...scenario, latest_single_run_at };
    })
    .filter((scenario): scenario is ScenarioWithSingleRuns => scenario !== null)
    .filter((scenario) => {
      if (!query) {
        return true;
      }
      return scenario.name.toLowerCase().includes(query) || scenario.description.toLowerCase().includes(query);
    })
    .sort((a, b) => b.latest_single_run_at.localeCompare(a.latest_single_run_at));
}

const TERMINAL_EVAL_RUN_STATUSES: EvalRunStatus[] = ['completed', 'soft_pass', 'failed', 'cancelled'];

export function isTerminalEvalRunStatus(status: EvalRunStatus | undefined): boolean {
  return status != null && TERMINAL_EVAL_RUN_STATUSES.includes(status);
}
export const TERMINAL_SCENARIO_RUN_STATUSES: ScenarioRunStatus[] = [
  'passed',
  'soft_pass',
  'failed',
  'skipped',
  'error',
  'cancelled',
];

const POLL_INTERVAL_MS = 4000;

export interface ScenarioRunDetail extends ScenarioRunSummary {
  step_results: Array<Record<string, unknown>>;
  error_message: string;
}

export interface TriggerEvalSuitePayload {
  tag_filter?: string[];
  agent_filter?: string[];
  mode?: string;
  overrides?: Record<string, unknown>;
}

export interface TriggerScenarioRunPayload {
  version_id?: string;
  overrides?: Record<string, unknown>;
}

export const evalStudioScenariosListQueryKey = (params?: Record<string, string | undefined>) =>
  ['eval-studio', 'scenarios', params] as const;

export const evalStudioScenarioRetrieveQueryKey = (id: string) => ['eval-studio', 'scenarios', id] as const;

export const evalStudioScenarioVersionsQueryKey = (id: string) => ['eval-studio', 'scenarios', id, 'versions'] as const;

export const evalStudioScenariosWithSingleRunsQueryKey = (params?: Record<string, string | undefined>) =>
  ['eval-studio', 'scenarios', 'with-single-runs', params] as const;

export const evalStudioScenarioTagsQueryKey = () => ['eval-studio', 'scenarios', 'tags'] as const;

export const evalStudioEvalRunsListQueryKey = (params?: Record<string, string | undefined>) =>
  ['eval-studio', 'eval-runs', params] as const;

export const evalStudioEvalRunRetrieveQueryKey = (id: string) => ['eval-studio', 'eval-runs', id] as const;

export async function fetchEvalStudioScenarios(params?: {
  status?: string;
  tag?: string;
  search?: string;
  agent_id?: string;
}): Promise<ScenarioListItem[]> {
  const response = await api.get<ScenarioListItem[]>('/api/v1/eval-studio/scenarios/', { params });
  return response.data;
}

export async function fetchEvalStudioScenario(id: string): Promise<ScenarioDetail> {
  const response = await api.get<ScenarioDetail>(`/api/v1/eval-studio/scenarios/${id}/`);
  return response.data;
}

export async function fetchEvalStudioScenarioTags(): Promise<string[]> {
  const response = await api.get<{ tags: string[] }>('/api/v1/eval-studio/scenarios/tags/');
  return response.data.tags;
}

export function useEvalStudioScenarioTags() {
  return useQuery({
    queryKey: evalStudioScenarioTagsQueryKey(),
    queryFn: fetchEvalStudioScenarioTags,
  });
}

export async function fetchEvalStudioScenarioVersions(id: string): Promise<ScenarioVersion[]> {
  const response = await api.get<ScenarioVersion[]>(`/api/v1/eval-studio/scenarios/${id}/versions/`);
  return response.data;
}

export async function fetchEvalStudioScenariosWithSingleRuns(params?: {
  search?: string;
}): Promise<ScenarioWithSingleRuns[]> {
  const response = await api.get<ScenarioWithSingleRuns[]>('/api/v1/eval-studio/scenarios/with-single-runs/', {
    params,
  });
  return response.data;
}

export async function fetchEvalStudioEvalRuns(params?: {
  status?: string;
  triggered_by?: string;
}): Promise<EvalRunListItem[]> {
  const response = await api.get<EvalRunListItem[]>('/api/v1/eval-studio/eval-runs/', { params });
  return response.data;
}

export async function fetchEvalStudioEvalRun(id: string): Promise<EvalRunDetail> {
  const response = await api.get<EvalRunDetail>(`/api/v1/eval-studio/eval-runs/${id}/`);
  return response.data;
}

export function useEvalStudioScenariosList(params?: {
  status?: string;
  tag?: string;
  search?: string;
  agent_id?: string;
}) {
  return useQuery({
    queryKey: evalStudioScenariosListQueryKey(params),
    queryFn: () => fetchEvalStudioScenarios(params),
  });
}

export function useEvalStudioScenariosByAgent(agentId: string | undefined, enabled = true) {
  const params = { agent_id: agentId };
  return useQuery({
    queryKey: evalStudioScenariosListQueryKey(params),
    queryFn: () => fetchEvalStudioScenarios(params),
    enabled: enabled && Boolean(agentId),
  });
}

export async function fetchEvalStudioTeammates(): Promise<AgentList[]> {
  const response = await api.get<AgentList[] | { results?: AgentList[] }>('/api/v1/hybrid/');
  if (Array.isArray(response.data)) {
    return response.data;
  }
  return response.data.results ?? [];
}

export function useEvalStudioTeammates() {
  return useQuery({
    queryKey: ['eval-studio', 'teammates'],
    queryFn: fetchEvalStudioTeammates,
  });
}

export function useEvalStudioScenarioRetrieve(id: string | null) {
  return useQuery({
    queryKey: evalStudioScenarioRetrieveQueryKey(id ?? ''),
    queryFn: () => fetchEvalStudioScenario(id!),
    enabled: Boolean(id),
  });
}

export function useEvalStudioScenarioVersions(id: string | null) {
  return useQuery({
    queryKey: evalStudioScenarioVersionsQueryKey(id ?? ''),
    queryFn: () => fetchEvalStudioScenarioVersions(id!),
    enabled: Boolean(id),
  });
}

export function useEvalStudioScenariosWithSingleRunsList(params?: { search?: string }) {
  const queryParams = {
    search: params?.search,
  };

  return useQuery({
    queryKey: evalStudioScenariosWithSingleRunsQueryKey(queryParams),
    queryFn: () => fetchEvalStudioScenariosWithSingleRuns(params),
  });
}

export function useEvalStudioEvalRunsList(params?: { status?: string; triggered_by?: string }) {
  return useQuery({
    queryKey: evalStudioEvalRunsListQueryKey(params),
    queryFn: () => fetchEvalStudioEvalRuns(params),
    refetchInterval: (query) => {
      const runs = query.state.data ?? [];
      const hasInFlight = runs.some((run) => run.status === 'queued' || run.status === 'running');
      if (!hasInFlight) {
        return false;
      }
      return POLL_INTERVAL_MS;
    },
  });
}

export function useEvalStudioEvalRunRetrieve(id: string | null) {
  return useQuery({
    queryKey: evalStudioEvalRunRetrieveQueryKey(id ?? ''),
    queryFn: () => fetchEvalStudioEvalRun(id!),
    enabled: Boolean(id),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status && TERMINAL_EVAL_RUN_STATUSES.includes(status)) {
        return false;
      }
      return POLL_INTERVAL_MS;
    },
  });
}

export function isTerminalScenarioRunStatus(status: ScenarioRunStatus | undefined): boolean {
  return Boolean(status && TERMINAL_SCENARIO_RUN_STATUSES.includes(status));
}

export const evalStudioScenarioRunRetrieveQueryKey = (id: string) => ['eval-studio', 'scenario-runs', id] as const;

export const evalStudioScenarioRunsListQueryKey = (params?: Record<string, string | undefined>) =>
  ['eval-studio', 'scenario-runs', 'list', params] as const;

export const evalStudioScenarioRunTraceQueryKey = (id: string) =>
  ['eval-studio', 'scenario-runs', id, 'trace'] as const;

export async function fetchEvalStudioScenarioRun(id: string): Promise<ScenarioRunDetail> {
  const response = await api.get<ScenarioRunDetail>(`/api/v1/eval-studio/scenario-runs/${id}/`);
  return response.data;
}

export async function fetchEvalStudioScenarioRuns(params?: {
  standalone?: boolean;
  scenario_id?: string;
  status?: string;
  search?: string;
}): Promise<ScenarioRunSummary[]> {
  const queryParams: Record<string, string> = {};
  if (params?.standalone) {
    queryParams.standalone = 'true';
  }
  if (params?.scenario_id) {
    queryParams.scenario_id = params.scenario_id;
  }
  if (params?.status) {
    queryParams.status = params.status;
  }
  if (params?.search) {
    queryParams.search = params.search;
  }
  const response = await api.get<ScenarioRunSummary[]>('/api/v1/eval-studio/scenario-runs/', {
    params: queryParams,
  });
  return response.data;
}

export async function fetchEvalStudioScenarioRunTrace(id: string): Promise<ScenarioRunTrace | null> {
  try {
    const response = await api.get<ScenarioRunTrace>(`/api/v1/eval-studio/scenario-runs/${id}/trace/`);
    return response.data;
  } catch (error) {
    if (typeof error === 'object' && error !== null && 'response' in error) {
      const status = (error as { response?: { status?: number } }).response?.status;
      if (status === 404) {
        return null;
      }
    }
    throw error;
  }
}

export function useEvalStudioScenarioRunRetrieve(id: string | null) {
  return useQuery({
    queryKey: evalStudioScenarioRunRetrieveQueryKey(id ?? ''),
    queryFn: () => fetchEvalStudioScenarioRun(id!),
    enabled: Boolean(id),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status && TERMINAL_SCENARIO_RUN_STATUSES.includes(status)) {
        return false;
      }
      return POLL_INTERVAL_MS;
    },
  });
}

export function useEvalStudioScenarioRunsList(params?: {
  standalone?: boolean;
  scenario_id?: string;
  status?: string;
  search?: string;
}) {
  const queryParams = {
    standalone: params?.standalone ? 'true' : undefined,
    scenario_id: params?.scenario_id,
    status: params?.status,
    search: params?.search,
  };

  return useQuery({
    queryKey: evalStudioScenarioRunsListQueryKey(queryParams),
    queryFn: () => fetchEvalStudioScenarioRuns(params),
    refetchInterval: (query) => {
      const runs = query.state.data ?? [];
      const hasInFlight = runs.some((run) => run.status === 'queued' || run.status === 'running');
      if (!hasInFlight) {
        return false;
      }
      return POLL_INTERVAL_MS;
    },
  });
}

export function useEvalStudioScenarioRunTrace(id: string | null) {
  return useQuery({
    queryKey: evalStudioScenarioRunTraceQueryKey(id ?? ''),
    queryFn: () => fetchEvalStudioScenarioRunTrace(id!),
    enabled: Boolean(id),
    retry: false,
  });
}

export interface EvalStudioRunUsageBucket {
  total_cost: string | null;
  total_tokens: number;
  request_count: number;
}

export interface EvalStudioRunUsage {
  scenario_runs: Record<string, EvalStudioRunUsageBucket>;
  total: EvalStudioRunUsageBucket;
}

export const evalStudioRunUsageQueryKey = (key: string) => ['eval-studio', 'run-usage', key] as const;

export async function fetchEvalStudioRunUsage(params: {
  evalRunId?: string | null;
  scenarioRunIds?: string[];
}): Promise<EvalStudioRunUsage> {
  const query: Record<string, string> = {};
  if (params.evalRunId) {
    query.eval_run_id = params.evalRunId;
  } else if (params.scenarioRunIds?.length) {
    query.scenario_run_ids = params.scenarioRunIds.join(',');
  }
  const response = await api.get<EvalStudioRunUsage>('/api/v1/eval-studio/runs/usage/', { params: query });
  return response.data;
}

/**
 * Aggregated LLM cost for scenario runs. Backend is vigilantai-only (403 for
 * other workspaces), so callers must gate `enabled` on the current workspace.
 * `live` keeps polling while the underlying runs are still executing so the
 * cost ticks up as turns complete.
 */
export function useEvalStudioRunUsage(
  params: { evalRunId?: string | null; scenarioRunIds?: string[] },
  options: { enabled: boolean; live?: boolean }
) {
  const key = params.evalRunId ?? (params.scenarioRunIds ?? []).join(',');
  return useQuery({
    queryKey: evalStudioRunUsageQueryKey(key),
    queryFn: () => fetchEvalStudioRunUsage(params),
    enabled: options.enabled && Boolean(key),
    refetchInterval: options.live ? POLL_INTERVAL_MS : false,
  });
}

export async function triggerEvalSuite(
  payload: TriggerEvalSuitePayload = {}
): Promise<{ eval_run_id: string; status: EvalRunStatus }> {
  const response = await api.post('/api/v1/eval-studio/eval-runs/trigger/', payload);
  return response.data;
}

export async function triggerScenarioRun(
  scenarioId: string,
  payload: TriggerScenarioRunPayload = {}
): Promise<{ scenario_run_id: string; status: ScenarioRunStatus }> {
  const response = await api.post(`/api/v1/eval-studio/scenarios/${scenarioId}/run/`, payload);
  return response.data;
}

export function useTriggerEvalSuite() {
  return useMutation({
    mutationFn: (payload: TriggerEvalSuitePayload = {}) => triggerEvalSuite(payload),
  });
}

export function useTriggerScenarioRun() {
  return useMutation({
    mutationFn: ({ scenarioId, payload }: { scenarioId: string; payload?: TriggerScenarioRunPayload }) =>
      triggerScenarioRun(scenarioId, payload),
  });
}

/**
 * Scenario mutations wrap the orval-generated hooks (see `@/gen-api/api`) and
 * add tenant-scoped query invalidation so the directory/editor refresh after a
 * lifecycle change or severity edit.
 */
function useInvalidateScenarioQueries() {
  const queryClient = useQueryClient();
  return (id?: string) => {
    queryClient.invalidateQueries({ queryKey: ['eval-studio', 'scenarios'] });
    queryClient.invalidateQueries({ queryKey: evalStudioScenarioTagsQueryKey() });
    if (id) {
      queryClient.invalidateQueries({ queryKey: evalStudioScenarioRetrieveQueryKey(id) });
      queryClient.invalidateQueries({ queryKey: evalStudioScenarioVersionsQueryKey(id) });
    }
  };
}

export function useUpdateScenario() {
  const invalidate = useInvalidateScenarioQueries();
  return useEvalStudioScenariosPartialUpdate({
    mutation: { onSuccess: (scenario) => invalidate(scenario.id) },
  });
}

export function usePublishScenario() {
  const invalidate = useInvalidateScenarioQueries();
  return useEvalStudioScenariosPublishCreate({
    mutation: { onSuccess: (scenario) => invalidate(scenario.id) },
  });
}

export function useDisableScenario() {
  const invalidate = useInvalidateScenarioQueries();
  return useEvalStudioScenariosDisableCreate({
    mutation: { onSuccess: (scenario) => invalidate(scenario.id) },
  });
}

export function useEnableScenario() {
  const invalidate = useInvalidateScenarioQueries();
  return useEvalStudioScenariosEnableCreate({
    mutation: { onSuccess: (scenario) => invalidate(scenario.id) },
  });
}

export function useArchiveScenario() {
  const invalidate = useInvalidateScenarioQueries();
  return useEvalStudioScenariosArchiveCreate({
    mutation: { onSuccess: (scenario) => invalidate(scenario.id) },
  });
}

export function useRestoreScenario() {
  const invalidate = useInvalidateScenarioQueries();
  return useEvalStudioScenariosRestoreCreate({
    mutation: { onSuccess: (scenario) => invalidate(scenario.id) },
  });
}
